/**
 * The kitchen as a drawing set, in primitives anything can render.
 *
 * A builder wants a dimensioned plan. A cabinet maker builds from elevations. A plumber and
 * an electrician want to know where the water, the waste, the gas and the power have to
 * land. All four are the same model seen four ways, so they are one list of primitives per
 * sheet and three renderers over it: SVG for the screen, jsPDF for paper, DXF for CAD.
 *
 * TWO UNITS, AND KEEPING THEM STRAIGHT IS THE WHOLE JOB.
 *
 *   Geometry is in MODEL millimetres -- the same numbers as the room, the runs and the
 *   schedule. A wall 4200 long is 4200 here.
 *
 *   Text size is in PAPER millimetres. A 2.5mm label is 2.5mm tall on the page whether the
 *   sheet is 1:50 or 1:20, because a label that scaled would be illegible on one and absurd
 *   on the other. So the SVG and PDF renderers use it as-is and the DXF renderer, which
 *   emits model units, divides it by the scale.
 *
 * `scale` is the drawing's denominator: 50 means 1:50, so a model millimetre is 1/50 of a
 * paper millimetre. `origin` is the model point that lands at the sheet's top-left margin.
 *
 * Pure. A plan in, strings and numbers out. No DOM, no three.js, no Vue.
 */
import * as room from './room.js'
import { offsets } from './layout.js'
import { schedule } from './schedule.js'
import { review } from './guidelines.js'
import { elevations } from './elevation.js'
import { services, SERVICE } from './services.js'
import { wallDims, angleDims, chain } from './dimension.js'

/** Paper sizes, landscape, in millimetres. */
export const SHEET = {
  a3: { w: 420, h: 297 },
  a4: { w: 297, h: 210 },
}

/** Layers, so a renderer can style a whole class of line at once and DXF can name them. */
export const LAYER = {
  wall: { colour: '#1C1917', width: 0.5 },
  cabinet: { colour: '#3F3A34', width: 0.25 },
  bench: { colour: '#1C1917', width: 0.35 },
  fitting: { colour: '#6B645C', width: 0.2 },
  dim: { colour: '#8A3E1E', width: 0.13 },
  text: { colour: '#1C1917', width: 0.13 },
  service: { colour: '#9A6B3F', width: 0.3 },
  frame: { colour: '#1C1917', width: 0.35 },
  faint: { colour: '#B8B0A6', width: 0.13 },
}

const MARGIN = 14          // paper mm, all round
const TITLE_H = 26         // paper mm, the strip along the bottom

/**
 * Every sheet, in order.
 *
 * @param {object} plan
 * @param {object} maker
 * @param {{ size?: 'a3'|'a4', name?: string }} opts
 * @returns {Array<{id, title, scale, size, origin, prims}>}
 */
export function sheets(plan, maker, opts = {}) {
  const size = SHEET[opts.size ?? 'a3'] ?? SHEET.a3
  const sched = schedule(plan, maker)
  const findings = review(plan, maker)
  const name = opts.name ?? plan.name ?? 'Kitchen'
  const out = []

  out.push(planSheet(plan, maker, { size, name, sched }))
  for (const e of elevations(plan, maker)) out.push(elevationSheet(e, { size, name }))
  out.push(servicesSheet(plan, maker, { size, name }))
  out.push(...scheduleSheets(sched, findings, { size, name, maker }))

  /*
   * ONE SCALE PER FAMILY, and the frame drawn last.
   *
   * A set whose every sheet found its own scale is a set you cannot read across: the plan
   * at 1:100, one elevation at 1:25 and the next at 1:50, each drawing a different size on
   * the same paper. A drawing set does not work that way. The plan and the services are the
   * same room seen twice, so they share a scale; the elevations share theirs. Within a
   * family the scale is the one that fits the LARGEST member, because a sheet with air in
   * it is merely plain and a sheet that overflows its frame is wrong.
   *
   * Which is why the title block is added here rather than by each builder: it carries the
   * ratio and the scale bar, and both are only known once the family has agreed.
   */
  harmonise(out, ['plan', 'services'])
  harmonise(out, out.filter((s) => s.id.startsWith('elev-')).map((s) => s.id))
  for (const s of out) place(s)

  return out.map((s, i) => ({ ...s, page: i + 1, pages: out.length }))
}

/** Give every named sheet the one scale that fits them all. */
function harmonise(all, ids) {
  const mine = all.filter((s) => s.box && ids.includes(s.id))
  if (mine.length < 2) return
  const scale = Math.max(...mine.map((s) => s.scale))
  for (const s of mine) s.scale = scale
}

/** Centre the drawing at its final scale, then draw the border and the title block over it. */
function place(s) {
  if (s.box) s.origin = centreOn(s.box, s.size, s.scale)
  frame(s.prims, s.size, { ...s.frameOpts, scale: s.frameOpts.scale === null ? null : s.scale })
}

// ── the plan ───────────────────────────────────────────────────────────────────────────

function planSheet(plan, maker, { size, name, sched }) {
  const lim = maker.limits
  const prims = []
  // The floor, so the room reads as a room and not as four lines.
  prims.push({ k: 'poly', layer: 'faint', pts: room.vertices(plan.walls), close: true, fill: true })

  // Walls, double-lined at their real thickness, broken over the openings.
  const thickness = lim.wallThickness ?? 90
  for (const w of plan.walls) {
    const n = room.inward(w)
    const L = room.length(w)
    const cuts = (plan.openings ?? []).filter((o) => o.wallId === w.id)
      .map((o) => ({ from: Math.max(0, o.at), to: Math.min(L, o.at + o.width) }))
      .filter((o) => o.to > o.from).sort((a, b) => a.from - b.from)
    let at = 0
    const band = (s, e) => {
      if (e - s < 1) return
      const a = room.at(w, s), b = room.at(w, e)
      const out2 = (p) => ({ x: p.x - n.x * thickness, y: p.y - n.y * thickness })
      prims.push({ k: 'poly', layer: 'wall', close: true, pts: [a, b, out2(b), out2(a)] })
    }
    for (const c of cuts) { band(at, c.from); at = Math.max(at, c.to) }
    band(at, L)

    // The openings themselves: a leaf and a swing for a door, a double line for a window.
    for (const o of cuts) {
      const from = o.from, to = o.to, width = to - from
      if ((plan.openings.find((x) => x.wallId === w.id && Math.abs(x.at - from) < 2)?.kind) === 'window') {
        for (const off of [-thickness * 0.66, -thickness * 0.33]) {
          prims.push({ k: 'line', layer: 'fitting',
            a: { x: room.at(w, from).x + n.x * off, y: room.at(w, from).y + n.y * off },
            b: { x: room.at(w, to).x + n.x * off, y: room.at(w, to).y + n.y * off } })
        }
      } else {
        const hinge = room.at(w, from)
        prims.push({ k: 'line', layer: 'fitting', a: hinge,
          b: { x: hinge.x + n.x * width, y: hinge.y + n.y * width } })
        prims.push({ k: 'arc', layer: 'faint', c: hinge, r: width,
          from: Math.atan2(n.y, n.x), to: Math.atan2(room.dir(w).y, room.dir(w).x) })
      }
    }
  }

  prims.push(...footprints(plan, maker))
  prims.push(...wallDims(plan))
  prims.push(...angleDims(plan))

  // Cabinet widths, as a chain along each base run: what a maker measures on site.
  for (const run of plan.runs.filter((r) => r.kind === 'base')) {
    const seg = room.segment(plan, run.wallId)
    if (!seg || !run.items.length) continue
    const at = offsets(run.items)
    const stops = [run.from, ...run.items.map((it, i) => run.from + at[i] + it.w)]
    const n = room.inward(seg)
    const off = room.isIsland(plan, run.wallId) ? -(seg.overhang ?? 0) - 260 : -260
    prims.push(...chain(stops.map((s) => room.at(seg, s)), {
      n: { x: -n.x, y: -n.y }, offset: Math.abs(off), layer: 'dim',
    }))
  }

  const box = boundsOfPrims(prims)
  return {
    id: 'plan', title: 'Plan', size, box, prims,
    scale: fitScale(box, size, SCALES),
    frameOpts: { title: name, sheet: 'Plan', note: `${sched.counts.cabinets} cabinets` },
  }
}

/** Every base and tall footprint, and the appliances that read at plan size. */
function footprints(plan, maker) {
  const lim = maker.limits
  const prims = []
  for (const run of plan.runs.filter((r) => r.kind === 'base')) {
    const seg = room.segment(plan, run.wallId)
    if (!seg) continue
    const n = room.inward(seg)
    const at = offsets(run.items)
    const isle = room.isIsland(plan, run.wallId)

    // The bench outline: the whole run in one line, so the drawing shows the benchtop and
    // the cabinet divisions separately, as a drawing does.
    if (run.items.length) {
      const a = room.at(seg, run.from), b = room.at(seg, run.to)
      const front = lim.baseDepth + lim.benchtopOverhang
      const backOff = isle ? -(seg.overhang ?? 0) : 0
      const o = (p, d) => ({ x: p.x + n.x * d, y: p.y + n.y * d })
      prims.push({ k: 'poly', layer: 'bench', close: true,
        pts: [o(a, backOff), o(b, backOff), o(b, front), o(a, front)] })
    }

    run.items.forEach((item, i) => {
      if (item.gap) return
      const spec = maker.cabinets.find((c) => c.id === item.specId)
      const s = run.from + at[i], e = s + item.w
      const depth = spec?.role === 'fridge' ? 700 : lim.baseDepth
      const a = room.at(seg, s), b = room.at(seg, e)
      const o = (p, d) => ({ x: p.x + n.x * d, y: p.y + n.y * d })
      prims.push({ k: 'poly', layer: 'cabinet', close: true, pts: [a, b, o(b, depth), o(a, depth)] })

      const mid = room.at(seg, (s + e) / 2)
      const c = { x: mid.x + n.x * depth * 0.55, y: mid.y + n.y * depth * 0.55 }
      if (spec?.role === 'sink') prims.push({ k: 'circle', layer: 'fitting', c, r: Math.min(item.w, depth) * 0.3 })
      if (spec?.role === 'cooktop') {
        for (const [u, v] of [[-0.22, -0.22], [0.22, -0.22], [-0.22, 0.22], [0.22, 0.22]]) {
          const d = room.dir(seg)
          prims.push({ k: 'circle', layer: 'fitting', r: 58,
            c: { x: c.x + d.x * u * item.w + n.x * v * depth, y: c.y + d.y * u * item.w + n.y * v * depth } })
        }
      }
      // The code, so the plan and the schedule are the same document -- and a filler says
      // FILL, which is what the schedule calls it, rather than being an unnamed strip.
      if (spec || item.filler) {
        prims.push({ k: 'text', layer: 'text', at: c, size: 2.2, align: 'centre',
          text: spec ? spec.id.toUpperCase() : 'FILL' })
      }
    })
  }
  return prims
}

// ── elevations ─────────────────────────────────────────────────────────────────────────

function elevationSheet(e, { size, name }) {
  const prims = e.prims.slice()
  /*
   * The box is measured off the drawing itself -- the wall, the cabinets, the height chain
   * off to the left and the width chain below -- and not guessed at from the wall's length.
   * A guess has to be generous to be safe, and generous is exactly what left the elevation
   * a small thing in the middle of a large page.
   *
   * In FLIPPED coordinates: v runs up from the floor, so -v runs down the page, and the box
   * has to be measured the way toPaper will read it.
   */
  const raw = boundsOfPrims(prims)
  const box = { minX: raw.minX, maxX: raw.maxX, minY: -raw.maxY, maxY: -raw.minY }
  return {
    id: `elev-${e.segId}`, title: e.title, size, box, prims, flipY: true,
    scale: fitScale(box, size, SCALES),
    frameOpts: { title: name, sheet: e.title, note: `${Math.round(e.length)}mm` },
  }
}

// ── services ───────────────────────────────────────────────────────────────────────────

function servicesSheet(plan, maker, { size, name }) {
  const bb = room.bounds(plan.walls)
  const prims = []
  prims.push({ k: 'poly', layer: 'faint', pts: room.vertices(plan.walls), close: true, fill: true })
  for (const w of plan.walls) {
    prims.push({ k: 'line', layer: 'wall', a: { x: w.x1, y: w.y1 }, b: { x: w.x2, y: w.y2 } })
  }
  // The cabinets, faint: they are context here, not the subject.
  for (const p of footprints(plan, maker)) {
    if (p.k === 'text') continue
    prims.push({ ...p, layer: 'faint' })
  }

  const list = services(plan, maker)
  for (const s of list) {
    prims.push({ k: 'circle', layer: 'service', c: s.at, r: 70 })
    prims.push({ k: 'text', layer: 'service', at: { x: s.at.x, y: s.at.y - 130 }, size: 2.4,
      align: 'centre', text: s.mark })
  }

  // A legend, in model coordinates along the bottom of the room.
  let y = bb.maxY + 320
  const kinds = [...new Set(list.map((s) => s.kind))]
  for (const kind of kinds) {
    const mine = list.filter((s) => s.kind === kind)
    prims.push({ k: 'text', layer: 'text', at: { x: bb.minX, y }, size: 2.4, align: 'left',
      text: `${mine[0].mark}  ${SERVICE[kind].name} — ${SERVICE[kind].note}` })
    y += 260
  }

  const box = boundsOfPrims(prims)
  return {
    id: 'services', title: 'Services', size, box, prims,
    scale: fitScale(box, size, SCALES),
    frameOpts: { title: name, sheet: 'Services',
      note: `${list.length} points — heights above finished floor` },
  }
}

// ── the schedule ───────────────────────────────────────────────────────────────────────

function scheduleSheets(sched, findings, { size, name, maker }) {
  /*
   * The schedule is a table, and a table is text at paper coordinates -- there is no model
   * geometry in it at all. So its "scale" is 1 and its origin is zero, and every position in
   * it is already paper millimetres. Which is the one place in this module where the two
   * units coincide, and saying so here is cheaper than a reader working it out.
   */
  const rows = sched.lines
  const usable = size.h - MARGIN * 2 - TITLE_H - 24
  const rowH = 5
  const perPage = Math.max(6, Math.floor(usable / rowH))
  const pages = Math.max(1, Math.ceil(rows.length / perPage))
  const out = []

  const cols = [
    { k: 'code', h: 'Code', w: 22 }, { k: 'name', h: 'Cabinet', w: 58 },
    { k: 'run', h: 'Run', w: 26 }, { k: 'w', h: 'W', w: 16 },
    { k: 'h', h: 'H', w: 16 }, { k: 'd', h: 'D', w: 16 },
    { k: 'door', h: 'Door', w: 50 }, { k: 'stretched', h: 'Note', w: 60 },
  ]

  for (let page = 0; page < pages; page++) {
    const prims = []
    let y = MARGIN + 10
    prims.push({ k: 'text', layer: 'text', at: { x: MARGIN, y }, size: 4.5, align: 'left', paper: true,
      text: pages > 1 ? `Cabinet schedule (${page + 1} of ${pages})` : 'Cabinet schedule' })
    y += 7
    prims.push({ k: 'text', layer: 'text', at: { x: MARGIN, y }, size: 2.6, align: 'left', paper: true,
      text: `${sched.counts.cabinets} cabinets · ${sched.finishes.doors} · ${sched.finishes.benchtop}, ${sched.benchtopMetres}m · ${sched.finishes.handles}` })
    y += 6

    let x = MARGIN
    for (const c of cols) {
      prims.push({ k: 'text', layer: 'text', at: { x, y }, size: 2.4, align: 'left', paper: true, text: c.h })
      x += c.w
    }
    y += 1.5
    prims.push({ k: 'line', layer: 'frame', paper: true,
      a: { x: MARGIN, y }, b: { x: size.w - MARGIN, y } })
    y += 4

    for (const l of rows.slice(page * perPage, (page + 1) * perPage)) {
      x = MARGIN
      for (const c of cols) {
        const v = l[c.k]
        prims.push({ k: 'text', layer: c.k === 'stretched' ? 'faint' : 'text', at: { x, y },
          size: 2.4, align: 'left', paper: true, text: v == null ? '' : String(v) })
        x += c.w
      }
      prims.push({ k: 'line', layer: 'faint', paper: true,
        a: { x: MARGIN, y: y + 1.4 }, b: { x: size.w - MARGIN, y: y + 1.4 } })
      y += rowH
    }

    // The designer's notes go on the last page, under the table: they are part of what the
    // maker is being told, and a note nobody reads is a note that did not happen.
    if (page === pages - 1 && findings.length) {
      y += 5
      prims.push({ k: 'text', layer: 'text', at: { x: MARGIN, y }, size: 3, align: 'left', paper: true,
        text: 'Design notes' })
      y += 5
      for (const f of findings.slice(0, 10)) {
        prims.push({ k: 'text', layer: f.level === 'fault' ? 'dim' : 'faint',
          at: { x: MARGIN, y }, size: 2.3, align: 'left', paper: true, wrap: size.w - MARGIN * 2,
          text: `${f.level === 'fault' ? 'MUST FIX' : 'NOTE'}  ${f.text}` })
        y += 4
      }
    }

    out.push({ id: `schedule-${page + 1}`, title: 'Cabinet schedule', scale: 1, size,
      origin: { x: 0, y: 0 }, prims, paperOnly: true, box: null,
      frameOpts: { title: name, sheet: 'Cabinet schedule', scale: null, note: maker.name } })
  }
  return out
}

// ── the sheet itself ───────────────────────────────────────────────────────────────────

/**
 * The border and the title block, in PAPER millimetres.
 *
 * `paper: true` on a primitive means its coordinates are already paper, so the renderers
 * skip the model transform for it. Everything in a title block is like that: it belongs to
 * the page, not to the kitchen.
 */
function frame(prims, size, { title, sheet, scale, note }) {
  const x0 = MARGIN / 2, y0 = MARGIN / 2
  const x1 = size.w - MARGIN / 2, y1 = size.h - MARGIN / 2
  prims.push({ k: 'poly', layer: 'frame', paper: true, close: true,
    pts: [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }] })
  const ty = y1 - TITLE_H
  prims.push({ k: 'line', layer: 'frame', paper: true, a: { x: x0, y: ty }, b: { x: x1, y: ty } })
  prims.push({ k: 'text', layer: 'text', paper: true, at: { x: x0 + 4, y: ty + 9 }, size: 4.2,
    align: 'left', text: title })
  prims.push({ k: 'text', layer: 'text', paper: true, at: { x: x0 + 4, y: ty + 16 }, size: 2.6,
    align: 'left', text: sheet })
  prims.push({ k: 'text', layer: 'faint', paper: true, at: { x: x0 + 4, y: ty + 22 }, size: 2.3,
    align: 'left', text: note ?? '' })
  if (scale) {
    prims.push({ k: 'text', layer: 'text', paper: true, at: { x: x1 - 4, y: ty + 9 }, size: 3.4,
      align: 'right', text: `1:${scale}` })
    // A scale bar, because a printed drawing is only as trustworthy as the bar on it: paper
    // gets photocopied and resized and the ratio in the corner stops being true.
    const barMm = scale >= 50 ? 1000 : 500
    const paperLen = barMm / scale
    const bx = x1 - 4 - paperLen, by = ty + 20
    prims.push({ k: 'line', layer: 'frame', paper: true, a: { x: bx, y: by }, b: { x: x1 - 4, y: by } })
    for (const t of [0, 0.5, 1]) {
      prims.push({ k: 'line', layer: 'frame', paper: true,
        a: { x: bx + paperLen * t, y: by - 1.6 }, b: { x: bx + paperLen * t, y: by + 1.6 } })
    }
    prims.push({ k: 'text', layer: 'faint', paper: true, at: { x: x1 - 4, y: by + 5 }, size: 2.2,
      align: 'right', text: `0 — ${barMm}mm` })
  }
  prims.push({ k: 'text', layer: 'faint', paper: true, at: { x: x1 - 4, y: ty - 3 }, size: 2.2,
    align: 'right', text: 'Sizes to be checked on site before manufacture' })
}

/**
 * The box the drawing actually occupies, in model millimetres.
 *
 * Every primitive that is not already in paper coordinates, including the dimension chains
 * and their labels, so the sheet is sized by what is on it. A text primitive contributes its
 * anchor only -- how wide the label ends up is a paper measurement, and LABEL below is the
 * allowance that covers it.
 */
function boundsOfPrims(prims) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  const hit = (x, y) => {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  for (const p of prims) {
    if (p.paper) continue
    if (p.k === 'line') { hit(p.a.x, p.a.y); hit(p.b.x, p.b.y) }
    else if (p.k === 'poly') { for (const q of p.pts) hit(q.x, q.y) }
    else if (p.k === 'circle' || p.k === 'arc') {
      hit(p.c.x - p.r, p.c.y - p.r); hit(p.c.x + p.r, p.c.y + p.r)
    } else if (p.k === 'text') hit(p.at.x, p.at.y)
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 1, maxY: 1 }
  return { minX, minY, maxX, maxY }
}

/** The scales a drawing may be at. A drawing at 1:37 is not a drawing. */
const SCALES = [1, 2, 5, 10, 20, 25, 50, 100]

/** Paper mm kept clear all round, for the half of a label that hangs past its anchor. */
const LABEL = 9

/** The largest of the offered scales at which the model box fits the drawing area. */
function fitScale(box, size, choices) {
  const w = Math.max(1, box.maxX - box.minX)
  const h = Math.max(1, box.maxY - box.minY)
  const availW = size.w - MARGIN * 2 - LABEL * 2
  const availH = size.h - MARGIN * 2 - TITLE_H - LABEL * 2
  for (const s of choices) {
    if (w / s <= availW && h / s <= availH) return s
  }
  return choices[choices.length - 1]
}

/** The model point that lands at the drawing area's top-left, so the drawing is centred. */
function centreOn(box, size, scale) {
  const w = (box.maxX - box.minX) / scale
  const h = (box.maxY - box.minY) / scale
  const availW = size.w - MARGIN * 2
  const availH = size.h - MARGIN * 2 - TITLE_H
  return {
    x: box.minX - (availW - w) / 2 * scale - MARGIN * scale,
    y: box.minY - (availH - h) / 2 * scale - MARGIN * scale,
  }
}

/**
 * Where a primitive's model point lands on the page, in paper millimetres.
 *
 * `flipY` is for an elevation, whose v counts UP from the floor while paper counts down the
 * page. The flip happens HERE, before the origin is subtracted, and it is the only place it
 * happens -- the first version left it to each renderer, which flipped after the shift and
 * put every elevation a metre above the top of its own sheet. An empty page with a title
 * block on it, three times over.
 */
export function toPaper(sheet, p) {
  const y = sheet.flipY ? -p.y : p.y
  return { x: (p.x - sheet.origin.x) / sheet.scale, y: (y - sheet.origin.y) / sheet.scale }
}

// ── SVG ────────────────────────────────────────────────────────────────────────────────

/**
 * One sheet as an SVG string, its viewBox in paper millimetres.
 *
 * Which means the same string prints at true scale and previews on screen, and a reader
 * measuring it with a ruler gets the right answer.
 */
export function toSvg(sheet, opts = {}) {
  const { w, h } = sheet.size
  const parts = [`<rect width="${w}" height="${h}" fill="${opts.background ?? '#FFFFFF'}"/>`]
  // toPaper does the elevation's flip; doing it again here is what emptied the sheet.
  const at = (p, paper) => (paper ? p : toPaper(sheet, p))
  const n = (v) => Math.round(v * 100) / 100
  const d = (pts, close, paper) => 'M' + pts.map((p) => {
    const q = at(p, paper)
    return `${n(q.x)} ${n(q.y)}`
  }).join(' L') + (close ? ' Z' : '')

  for (const p of sheet.prims) {
    const L = LAYER[p.layer] ?? LAYER.cabinet
    const stroke = `stroke="${L.colour}" stroke-width="${L.width}" fill="${p.fill ? '#F4F1EC' : 'none'}"`
    if (p.k === 'line') parts.push(`<path d="${d([p.a, p.b], false, p.paper)}" ${stroke}/>`)
    else if (p.k === 'poly') parts.push(`<path d="${d(p.pts, p.close, p.paper)}" ${stroke} stroke-linejoin="round"/>`)
    else if (p.k === 'circle') {
      const c = at(p.c, p.paper)
      parts.push(`<circle cx="${n(c.x)}" cy="${n(c.y)}" r="${n(p.paper ? p.r : p.r / sheet.scale)}" ${stroke}/>`)
    } else if (p.k === 'arc') {
      const c = at(p.c, p.paper)
      const r = p.paper ? p.r : p.r / sheet.scale
      const a0 = at({ x: p.c.x + Math.cos(p.from) * p.r, y: p.c.y + Math.sin(p.from) * p.r }, p.paper)
      const a1 = at({ x: p.c.x + Math.cos(p.to) * p.r, y: p.c.y + Math.sin(p.to) * p.r }, p.paper)
      void c
      parts.push(`<path d="M${n(a0.x)} ${n(a0.y)}A${n(r)} ${n(r)} 0 0 1 ${n(a1.x)} ${n(a1.y)}" ${stroke}/>`)
    } else if (p.k === 'text') {
      const q = at(p.at, p.paper)
      const anchor = p.align === 'centre' ? 'middle' : p.align === 'right' ? 'end' : 'start'
      const rot = p.rot ? ` transform="rotate(${n(p.rot)} ${n(q.x)} ${n(q.y)})"` : ''
      parts.push(`<text x="${n(q.x)}" y="${n(q.y)}" font-family="Helvetica, Arial, sans-serif"`
        + ` font-size="${n(p.size)}" fill="${L.colour}" text-anchor="${anchor}"${rot}>${esc(p.text)}</text>`)
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}mm" height="${h}mm">`
    + parts.join('') + '</svg>'
}

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
